import * as React from "react";

/**
 * How many pills fit on one line, measured rather than guessed.
 *
 * ── WHY THIS TAKES THREE PASSES AND NOT ONE ───────────────────────────────────────────────
 *
 * The count depends on the widths, the widths depend on what is rendered, and what is rendered
 * depends on the count. Measuring once gives the wrong answer whenever showing the "+n" overflow
 * badge changes how much room is left — the badge itself takes space, so the honest sequence is:
 *
 *   1. render every pill, so the real widths exist to measure
 *   2. measure, and render that count (which may now add the overflow badge)
 *   3. if a badge appeared, measure again — there is less room than in step 2
 *
 * Each pass has to reach the DOM before the next one reads it, which is why this is a generator
 * driven from a layout effect rather than a loop. A plain loop would measure the same stale
 * layout three times and return confidently wrong numbers.
 */
export function useTruncatePills({ enable, pills }: { enable?: boolean; pills: string[] }) {
  const pillListRef = React.useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = React.useState(pills.length);
  const passesRef = React.useRef<Generator<number> | null>(null);

  const measure = React.useCallback(() => {
    const pillList = pillListRef.current;
    if (!pillList) return pills.length;

    const items = Array.from(pillList.querySelectorAll<HTMLElement>('[role="listitem"]'));
    const maxWidth = pillList.getBoundingClientRect().width;
    const listGap = Number.parseInt(window.getComputedStyle(pillList).gap, 10) || 0;
    // The badge is already on screen when we re-measure, so its width is charged UP FRONT —
    // otherwise the last pill claims room the badge is about to take.
    const showingOverflow = pillList.querySelector("[data-overflowindicator]");

    let used = 0;
    let fits = 0;
    if (showingOverflow) {
      const badge = items.pop();
      if (badge) used += badge.getBoundingClientRect().width + listGap;
    }
    for (const item of items) {
      used += item.getBoundingClientRect().width + listGap;
      // Rounded on BOTH sides: sub-pixel layout means a pill that visually fits exactly can
      // measure a hundredth of a pixel over and get dropped for no reason a user could see.
      if (Math.round(used) <= Math.round(maxWidth)) fits++;
      else break;
    }
    return fits;
  }, [pills.length]);

  const run = React.useCallback(() => {
    if (!enable) return;
    passesRef.current = (function* passes() {
      yield pills.length; // pass 1: render them all so there is something to measure
      const measured = measure();
      const badgeAppeared = measured < pills.length && measured > 0;
      yield measured; // pass 2: render that many, possibly adding the overflow badge
      if (badgeAppeared) yield measure(); // pass 3: the badge took room, so re-measure
    })();
    const first = passesRef.current.next();
    if (!first.done) setVisibleCount(first.value);
  }, [enable, measure, pills.length]);

  React.useLayoutEffect(() => {
    run();
  }, [run]);

  /*
   * Advance one pass per committed layout, so each measurement reads the DOM the previous pass
   * produced rather than the one before it. Deliberately dependency-free: it must run after
   * EVERY commit, which is the mechanism.
   *
   * It cannot loop forever — the generator yields at most three times and then reports `done`,
   * at which point the ref is cleared and this returns immediately. The lint rule cannot see
   * that the update source is finite.
   */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  React.useLayoutEffect(() => {
    const generator = passesRef.current;
    if (!generator) return;
    const next = generator.next();
    if (next.done) {
      passesRef.current = null;
      return;
    }
    setVisibleCount((previous) => (previous === next.value ? previous : next.value));
  });

  React.useEffect(() => {
    const element = pillListRef.current;
    if (!element || !enable || typeof ResizeObserver === "undefined") return undefined;
    // A field that gets narrower must drop pills; without this the row silently overflows.
    const observer = new ResizeObserver(() => run());
    observer.observe(element);
    return () => observer.disconnect();
  }, [enable, run]);

  return {
    pillListRef,
    visibleCount,
    visiblePills: enable ? pills.slice(0, visibleCount) : pills,
  };
}
